import { useEffect, useRef, useState } from "react";
import "../styles/FishSwimmer.css";
import {
  SWIMMERS,
  SPAWN_CONFIG,
  SWIM_CONFIG,
} from "../config/swimmersConfig.js";

const randomBetween = ({ min, max }) => min + Math.random() * (max - min);

export default function FishSwimmer() {
  const [active, setActive] = useState([]);
  const activeRef = useRef([]); // always holds the latest list for the timers

  // builds a school of fish swimmers
  function buildSchool({ sources, count, fishWidth, spread }) {
    const n = Math.round(randomBetween(count));
    return Array.from({ length: n }, () => ({
      src: sources[Math.floor(Math.random() * sources.length)],
      width: randomBetween(fishWidth),
      x: Math.random() * spread.x,
      y: Math.random() * spread.y,
      bobDelay: -Math.random() * 3, // negative = already mid-bob at spawn
    }));
  }

  useEffect(() => {
    let checkTimer;
    let cancelled = false;
    const removeTimers = new Set();

    function updateActive(next) {
      activeRef.current = next;
      setActive(next);
    }

    function spawnCheck() {
      if (cancelled) return;

      const current = activeRef.current;

      // only types that aren't already swimming
      const available = SWIMMERS.filter(
        (s) => !current.some((a) => a.type === s.type),
      );

      const chance = Math.pow(SPAWN_CONFIG.decay, current.length);

      if (available.length > 0 && Math.random() < chance) {
        const picked = available[Math.floor(Math.random() * available.length)];
        const duration = randomBetween(picked.duration ?? SWIM_CONFIG.duration);
        const top = randomBetween(picked.top ?? SWIM_CONFIG.top);
        const key = `${picked.type}-${Date.now()}`;

        const school = picked.school;

        updateActive([
          ...current,
          {
            key,
            type: picked.type,
            src: picked.src,
            duration,
            top,
            width: school
              ? school.spread.x + school.fishWidth.max
              : SWIM_CONFIG.baseWidth * (picked.scale ?? 1),
            height: school ? school.spread.y + school.fishWidth.max : undefined,
            members: school ? buildSchool(school) : null,
            direction: Math.random() < 0.5 ? 1 : -1,
          },
        ]);

        const removeTimer = setTimeout(() => {
          removeTimers.delete(removeTimer);
          updateActive(activeRef.current.filter((s) => s.key !== key));
        }, duration * 1000);
        removeTimers.add(removeTimer);
      }

      checkTimer = setTimeout(
        spawnCheck,
        randomBetween(SPAWN_CONFIG.checkDelay),
      );
    }

    checkTimer = setTimeout(spawnCheck, SPAWN_CONFIG.firstCheckDelay);

    return () => {
      cancelled = true;
      clearTimeout(checkTimer);
      removeTimers.forEach(clearTimeout);
    };
  }, []);

  return (
    <>
      {active.map((s) => (
        <div
          key={s.key}
          className={`fish-swimmer ${s.direction === -1 ? "reverse" : ""}`}
          style={{
            top: `${s.top}vh`,
            "--swim-duration": `${s.duration}s`,
            "--swimmer-width": `${s.width}px`,
          }}
        >
          {s.members ? (
            <div
              className="school"
              style={{ width: s.width, height: s.height }}
            >
              {s.members.map((m, i) => (
                <img
                  key={i}
                  className="school-fish"
                  src={m.src}
                  alt=""
                  style={{
                    left: m.x,
                    top: m.y,
                    width: m.width,
                    maxWidth: "none",
                    animationDelay: `${m.bobDelay}s`,
                  }}
                />
              ))}
            </div>
          ) : (
            <img
              src={s.src}
              alt=""
              style={{ width: `${s.width}px`, maxWidth: "none" }}
            />
          )}
        </div>
      ))}
    </>
  );
}
